import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { api } from "../api/client";
import type { QueryKey } from "@tanstack/react-query";
import { keys } from "../api/query-registry";
import { hkToday, shiftDate } from "../lib/dates";
import type { ResidentItem } from "./useResidents";
import type { RotaDay } from "./useRota";
import type { TodoTemplate } from "./useTemplates";
import type { Todo } from "./useTodos";

export interface SearchResult {
  id: string;
  type: "resident" | "todo" | "template" | "rota" | "log";
  title: string;
  subtitle: string;
  to: string;
}

export type SearchGroup = { type: SearchResult["type"]; label: string; results: SearchResult[] };

interface RotaList {
  month: string;
  days: RotaDay[];
}

interface DailyLogData {
  date: string;
  notes: string;
  completedTodos: { id: string; title: string; homeId: string | null; completedAt: string }[];
}

const MAX_PER_GROUP = 6;
/** Plain-text view of a day-note (stored as HTML — ADR-0010) for search + snippets. */
function textOf(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

function monthDays(month: string): string[] {
  const [y, m] = month.split("-").map(Number);
  const first = `${month}-01`;
  const daysInMonth = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
  return Array.from({ length: daysInMonth }, (_, i) => shiftDate(first, i));
}

/** V1 client-side search over already-fetched data — no search API (docs/03-UX-SPEC.md). */
export function useGlobalSearch() {
  const qc = useQueryClient();

  const warmUp = useCallback(() => {
    void qc.prefetchQuery({
      queryKey: keys.searchResidents(),
      queryFn: () => api<{ items: ResidentItem[] }>("/api/residents?status=all"),
      staleTime: 30_000,
    });
    void qc.prefetchQuery({
      queryKey: keys.searchTodos(),
      queryFn: () => api<{ items: Todo[] }>("/api/todos?pageSize=100"),
      staleTime: 30_000,
    });
    void qc.prefetchQuery({
      queryKey: keys.templates(),
      queryFn: () => api<TodoTemplate[]>("/api/templates"),
      staleTime: 30_000,
    });
    const today = hkToday();
    const months = new Set<string>();
    for (let i = -1; i <= 1; i += 1) {
      const [y, m] = today.split("-").map(Number);
      const d = new Date(Date.UTC(y!, m! - 1 + i, 1));
      months.add(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
    }
    for (const month of months) {
      void qc.prefetchQuery({
        queryKey: keys.rotaList(month),
        queryFn: () => api<RotaList>(`/api/rota?month=${month}`),
        staleTime: 30_000,
      });
    }
    // Daily-log notes are only searched within the current month (V1, small dataset).
    for (const date of monthDays(today.slice(0, 7))) {
      void qc.prefetchQuery({
        queryKey: keys.dailyLog(date),
        queryFn: () => api<DailyLogData>(`/api/daily-logs/${date}`),
        staleTime: 30_000,
      });
    }
  }, [qc]);

  const search = useCallback(
    (query: string): SearchGroup[] => {
      const q = query.trim().toLowerCase();
      if (!q) return [];

      const groups: SearchGroup[] = [];

      const residents = qc.getQueryData<{ items: ResidentItem[] }>(keys.searchResidents())?.items ?? [];
      const residentHits = residents
        .filter((r) => r.name.toLowerCase().includes(q) || (r.bed ?? "").toLowerCase().includes(q))
        .slice(0, MAX_PER_GROUP)
        .map<SearchResult>((r) => ({
          id: `resident-${r.id}`,
          type: "resident",
          title: r.name,
          subtitle: [r.homeName, r.bed].filter(Boolean).join(" · "),
          to: `/residents/${r.id}`, // quick-view (Stage C)
        }));
      if (residentHits.length > 0) groups.push({ type: "resident", label: "Residents", results: residentHits });

      const todos = qc.getQueryData<{ items: Todo[] }>(keys.searchTodos())?.items ?? [];
      const todoHits = todos
        .filter((t) => [t.title, t.notes ?? "", t.tags.join(" ")].some((s) => s.toLowerCase().includes(q)))
        .slice(0, MAX_PER_GROUP)
        .map<SearchResult>((t) => ({
          id: `todo-${t.id}`,
          type: "todo",
          title: t.title,
          subtitle: `${t.status === "done" ? "Done" : "Open"}${t.dueDate ? ` · due ${t.dueDate.slice(0, 10)}` : ""}`,
          to: "/todos",
        }));
      if (todoHits.length > 0) groups.push({ type: "todo", label: "To-dos", results: todoHits });

      const templates = qc.getQueryData<TodoTemplate[]>(keys.templates()) ?? [];
      const templateHits = templates
        .filter((t) => t.title.toLowerCase().includes(q))
        .slice(0, MAX_PER_GROUP)
        .map<SearchResult>((t) => ({
          id: `template-${t.id}`,
          type: "template",
          title: t.title,
          subtitle: ["template", t.category].filter(Boolean).join(" · "),
          to: "/todos",
        }));
      if (templateHits.length > 0) groups.push({ type: "template", label: "Templates", results: templateHits });

      const homes = qc.getQueryData<{ id: string; name: string }[]>(keys.homes()) ?? [];
      const rotaHits: SearchResult[] = [];
      const today = hkToday();
      const months = new Set<string>();
      for (let i = -1; i <= 1; i += 1) {
        const [y, m] = today.split("-").map(Number);
        const d = new Date(Date.UTC(y!, m! - 1 + i, 1));
        months.add(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
      }
      for (const month of months) {
        const list = qc.getQueryData<RotaList>(keys.rotaList(month));
        for (const day of list?.days ?? []) {
          const homeName = homes.find((h) => h.id === day.homeId)?.name ?? day.homeId;
          const hay = [homeName, day.marker ?? "", day.startTime, day.endTime, day.date].join(" ");
          if (!hay.toLowerCase().includes(q)) continue;
          rotaHits.push({
            id: `rota-${day.id}`,
            type: "rota",
            title: `${day.date} · ${homeName}`,
            subtitle: `${day.startTime.slice(0, 5)}–${day.endTime.slice(0, 5)}${day.marker ? ` · ${day.marker}` : ""}`,
            to: `/rota?day=${day.date}`,
          });
        }
      }
      if (rotaHits.length > 0) groups.push({ type: "rota", label: "Rota", results: rotaHits.slice(0, MAX_PER_GROUP) });

      const logHits: SearchResult[] = [];
      const todayMonth = today.slice(0, 7);
      for (const date of monthDays(todayMonth)) {
        const log = qc.getQueryData<DailyLogData>(keys.dailyLog(date));
        if (!log?.notes) continue;
        const plain = textOf(log.notes);
        if (!plain.toLowerCase().includes(q)) continue;
        logHits.push({
          id: `log-${date}`,
          type: "log",
          title: date,
          subtitle: plain.slice(0, 80),
          to: `/log/${date}`,
        });
      }
      if (logHits.length > 0) groups.push({ type: "log", label: "Day notes", results: logHits.slice(0, MAX_PER_GROUP) });

      return groups;
    },
    [qc],
  );

  // Datasets the search reads, in the same shape warmUp prefetches. "ready" is
  // false only while a first load is still in flight, so the palette can show
  // "Searching…" instead of a false "No matches" (the first-query race).
  const searchKeys = useMemo(() => {
    const today = hkToday();
    const months = new Set<string>();
    for (let i = -1; i <= 1; i += 1) {
      const [y, m] = today.split("-").map(Number);
      const d = new Date(Date.UTC(y!, m! - 1 + i, 1));
      months.add(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
    }
    const datasets: QueryKey[] = [keys.searchResidents(), keys.searchTodos(), keys.templates(), keys.homes()];
    for (const month of months) datasets.push(keys.rotaList(month));
    for (const date of monthDays(today.slice(0, 7))) datasets.push(keys.dailyLog(date));
    return new Set(datasets.map((k) => JSON.stringify(k)));
  }, []);
  const fetching = useIsFetching({ predicate: (q) => searchKeys.has(JSON.stringify(q.queryKey)) });
  const ready = fetching === 0;

  return { warmUp, search, ready };
}
